import { differenceInDays, differenceInHours } from "date-fns";
import { getAlertId } from "../../helpers/alert";
import { getDistance } from "../../helpers/geo";
import { Alert } from "../alerts/alertsSlice";
import {
  AltitudeType,
  HeightUnit,
  TemperatureUnit,
  DistanceUnit,
  TimeFormat,
  SpeedUnit,
  AltitudeLevels,
  OnOff,
} from "../../features/rap/extra/settings/settingEnums";
import {
  DEFAULT_DISTANCE_UNIT,
  DEFAULT_HEIGHT_UNIT,
  DEFAULT_SPEED_UNIT,
  DEFAULT_TEMPERATURE_UNIT,
  DEFAULT_TIME_FORMAT,
} from "../../helpers/locale";
import { Languages } from "../../i18n";
import {
  DEFAULT_SKILL_PRESET,
  FLYABILITY_PRESETS,
  type FlyabilityThresholds,
  type SkillPreset,
} from "../flyability/flyability";

export interface UserLocation {
  lat: number;
  lon: number;
  label: string;
  lastVisited: number;
  isFallbackLabel?: boolean;
}

import {
  LOCATIONS_STORAGE_KEY,
  ALTITUDE_STORAGE_KEY,
  ALTITUDE_LEVELS_STORAGE_KEY,
  HEIGHT_UNIT_STORAGE_KEY,
  SPEED_UNIT_STORAGE_KEY,
  TEMPERATURE_UNIT_STORAGE_KEY,
  DISTANCE_UNIT_STORAGE_KEY,
  TIME_FORMAT_STORAGE_KEY,
  DISCUSSION_LAST_VIEWED_STORAGE_KEY,
  READ_ALERTS,
  HIDDEN_ALERTS,
  SWIPE_INERTIA_STORAGE_KEY,
  G_AIRMET_READ_STORAGE_KEY,
  LANGUAGE_STORAGE_KEY,
  ADVANCED_STORAGE_KEY,
  FLYABILITY_SKILL_STORAGE_KEY,
  FLYABILITY_THRESHOLDS_STORAGE_KEY,
  MAX_LOCATIONS,
  MAX_DISTANCE_MATCH,
} from "./constants";

export function getLocations(): UserLocation[] {
  const locations: UserLocation[] = JSON.parse(
    localStorage.getItem(LOCATIONS_STORAGE_KEY) || "[]",
  );

  return locations;
}

export function visitedLocation(location: UserLocation): UserLocation[] {
  const locations = getLocations();

  locations.sort((a, b) => getDistance(a, location) - getDistance(b, location));

  if (
    !locations.length ||
    getDistance(locations[0], location) > MAX_DISTANCE_MATCH
  ) {
    locations.push(location);
  } else {
    locations[0] = {
      ...location,

      // If the API failed, don't update stored location with fallback label
      label: location.isFallbackLabel
        ? locations[0].label || location.label
        : location.label,
    };
  }

  const recentLocations = locations
    .sort((a, b) => b.lastVisited - a.lastVisited)
    .slice(0, MAX_LOCATIONS);

  localStorage.setItem(LOCATIONS_STORAGE_KEY, JSON.stringify(recentLocations));

  return recentLocations;
}

export function removeLocation(location: UserLocation): UserLocation[] {
  const locations = getLocations();

  const index = locations.findIndex(
    ({ lat, lon }) => location.lat === lat && location.lon === lon,
  );

  if (index === -1) return locations;

  locations.splice(index, 1);

  localStorage.setItem(LOCATIONS_STORAGE_KEY, JSON.stringify(locations));

  return locations;
}

export function findLocation(
  coords: GeolocationCoordinates,
): UserLocation | undefined {
  const locations = getLocations();
  const target = { lat: coords.latitude, lon: coords.longitude };

  locations.sort((a, b) => getDistance(a, target) - getDistance(b, target));

  if (
    locations.length &&
    getDistance(locations[0], target) <= MAX_DISTANCE_MATCH
  ) {
    return locations[0];
  }
}

export function getAltitude(): AltitudeType {
  const savedValue = localStorage.getItem(ALTITUDE_STORAGE_KEY);

  if (
    typeof savedValue !== "string" ||
    (savedValue !== AltitudeType.AGL &&
      savedValue !== AltitudeType.MSL &&
      savedValue !== AltitudeType.Pressure)
  )
    return AltitudeType.AGL;

  return savedValue;
}

export function setAltitudeLevels(altitudeLevels: AltitudeLevels): void {
  localStorage.setItem(ALTITUDE_LEVELS_STORAGE_KEY, altitudeLevels);
}

export function getAltitudeLevels(): AltitudeLevels {
  const savedValue = localStorage.getItem(ALTITUDE_LEVELS_STORAGE_KEY);

  if (
    typeof savedValue !== "string" ||
    (savedValue !== AltitudeLevels.Raw &&
      savedValue !== AltitudeLevels.Normalized)
  )
    return AltitudeLevels.Normalized;

  return savedValue;
}

export function setAltitude(altitude: AltitudeType): void {
  localStorage.setItem(ALTITUDE_STORAGE_KEY, altitude);
}

export function getHeightUnit(): HeightUnit {
  const savedValue = localStorage.getItem(HEIGHT_UNIT_STORAGE_KEY);

  if (
    typeof savedValue !== "string" ||
    (savedValue !== HeightUnit.Feet && savedValue !== HeightUnit.Meters)
  )
    return DEFAULT_HEIGHT_UNIT;

  return savedValue;
}

export function setHeightUnit(heightUnit: HeightUnit): void {
  localStorage.setItem(HEIGHT_UNIT_STORAGE_KEY, heightUnit);
}

export function getSpeedUnit(): SpeedUnit {
  const savedValue = localStorage.getItem(SPEED_UNIT_STORAGE_KEY);

  if (
    typeof savedValue !== "string" ||
    (savedValue !== SpeedUnit.KPH &&
      savedValue !== SpeedUnit.Knots &&
      savedValue !== SpeedUnit.MPH &&
      savedValue !== SpeedUnit.mps)
  )
    return DEFAULT_SPEED_UNIT;

  return savedValue;
}

export function setSpeedUnit(speedUnit: SpeedUnit): void {
  localStorage.setItem(SPEED_UNIT_STORAGE_KEY, speedUnit);
}

export function getTemperatureUnit(): TemperatureUnit {
  const savedValue = localStorage.getItem(TEMPERATURE_UNIT_STORAGE_KEY);

  if (
    typeof savedValue !== "string" ||
    (savedValue !== TemperatureUnit.Celsius &&
      savedValue !== TemperatureUnit.Fahrenheit)
  )
    return DEFAULT_TEMPERATURE_UNIT;

  return savedValue;
}

export function setTemperatureUnit(temperatureUnit: TemperatureUnit): void {
  localStorage.setItem(TEMPERATURE_UNIT_STORAGE_KEY, temperatureUnit);
}

export function getDistanceUnit(): DistanceUnit {
  const savedValue = localStorage.getItem(DISTANCE_UNIT_STORAGE_KEY);

  if (
    typeof savedValue !== "string" ||
    (savedValue !== DistanceUnit.Kilometers &&
      savedValue !== DistanceUnit.Miles)
  )
    return DEFAULT_DISTANCE_UNIT;

  return savedValue;
}

export function setDistanceUnit(distanceUnit: DistanceUnit): void {
  localStorage.setItem(DISTANCE_UNIT_STORAGE_KEY, distanceUnit);
}

export function getTimeFormat(): TimeFormat {
  const savedValue = localStorage.getItem(TIME_FORMAT_STORAGE_KEY);

  if (
    typeof savedValue !== "string" ||
    (savedValue !== TimeFormat.Twelve && savedValue !== TimeFormat.TwentyFour)
  )
    return DEFAULT_TIME_FORMAT;

  return savedValue;
}

export function setTimeFormat(timeFormat: TimeFormat): void {
  localStorage.setItem(TIME_FORMAT_STORAGE_KEY, timeFormat);
}

export function discussionLastViewedByStation(): Record<string, string> {
  const savedValue = localStorage.getItem(DISCUSSION_LAST_VIEWED_STORAGE_KEY);

  if (typeof savedValue !== "string") return {};

  return JSON.parse(savedValue);
}

export function setDiscussionViewed(stationId: string, date: string) {
  const discussionsLastViewed = discussionLastViewedByStation();

  Object.keys(discussionsLastViewed).forEach((stationId) => {
    if (
      differenceInHours(
        new Date(discussionsLastViewed[stationId]),
        new Date(),
      ) > 24
    )
      delete discussionsLastViewed[stationId];
  });

  discussionsLastViewed[stationId] = date;

  localStorage.setItem(
    DISCUSSION_LAST_VIEWED_STORAGE_KEY,
    JSON.stringify(discussionsLastViewed),
  );
}

export function getReadAlerts(): Record<string, string> {
  const savedValue = localStorage.getItem(READ_ALERTS);

  if (typeof savedValue !== "string") return {};

  return JSON.parse(savedValue);
}

export function setReadAlert(alert: Alert): Record<string, string> {
  const readAlerts = getReadAlerts();

  readAlerts[getAlertId(alert)] = new Date().toISOString();

  // Cleanup old entries
  Object.entries(readAlerts).forEach(([key, dateString]) => {
    if (differenceInDays(new Date(), new Date(dateString)) > 30) {
      delete readAlerts[key];
    }
  });

  localStorage.setItem(READ_ALERTS, JSON.stringify(readAlerts));

  return readAlerts;
}

export function getHiddenAlerts(): Record<string, true> {
  const savedValue = localStorage.getItem(HIDDEN_ALERTS);

  if (typeof savedValue !== "string") return {};

  return JSON.parse(savedValue);
}

export function setHiddenAlert(alert: Alert): Record<string, true> {
  const hiddenAlerts = getHiddenAlerts();

  hiddenAlerts[getAlertId(alert)] = true;

  localStorage.setItem(HIDDEN_ALERTS, JSON.stringify(hiddenAlerts));

  return hiddenAlerts;
}

export function resetHiddenAlerts() {
  localStorage.removeItem(HIDDEN_ALERTS);
}

export function getSwipeInertia(): OnOff {
  const savedValue = localStorage.getItem(SWIPE_INERTIA_STORAGE_KEY);

  if (
    typeof savedValue !== "string" ||
    (savedValue !== OnOff.On && savedValue !== OnOff.Off)
  )
    return OnOff.On;

  return savedValue;
}

export function setSwipeInertia(swipeInertia: OnOff): OnOff {
  localStorage.setItem(SWIPE_INERTIA_STORAGE_KEY, swipeInertia);

  return swipeInertia;
}

export function getGAirmetRead(): OnOff {
  const savedValue = localStorage.getItem(G_AIRMET_READ_STORAGE_KEY);

  if (
    typeof savedValue !== "string" ||
    (savedValue !== OnOff.On && savedValue !== OnOff.Off)
  )
    return OnOff.Off;

  return savedValue;
}

export function setGAirmetRead(gAirmetRead: OnOff): OnOff {
  localStorage.setItem(G_AIRMET_READ_STORAGE_KEY, gAirmetRead);

  return gAirmetRead;
}

export function getLanguage(): Languages {
  const savedValue = localStorage.getItem(LANGUAGE_STORAGE_KEY);

  if (
    savedValue !== Languages.Auto &&
    savedValue !== Languages.EN &&
    savedValue !== Languages.FR &&
    savedValue !== Languages.NL &&
    savedValue !== Languages.ES &&
    savedValue !== Languages.DE
  )
    // Default to French (École Paramoteur Mauricie, Québec) when nothing is
    // stored yet. A saved choice (incl. English) is always respected.
    return Languages.FR;

  return savedValue;
}

export function setLanguage(language: Languages): void {
  localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
}

export function getAdvanced(): OnOff {
  const savedValue = localStorage.getItem(ADVANCED_STORAGE_KEY);

  if (
    typeof savedValue !== "string" ||
    (savedValue !== OnOff.Off && savedValue !== OnOff.On)
  )
    // Advanced features on by default (École Paramoteur Mauricie preference).
    return OnOff.On;

  return savedValue;
}

export function setAdvanced(advanced: OnOff): void {
  localStorage.setItem(ADVANCED_STORAGE_KEY, advanced);
}

export function getFlyabilitySkill(): SkillPreset {
  const savedValue = localStorage.getItem(FLYABILITY_SKILL_STORAGE_KEY);

  if (
    savedValue !== "beginner" &&
    savedValue !== "intermediate" &&
    savedValue !== "advanced"
  )
    return DEFAULT_SKILL_PRESET;

  return savedValue;
}

export function setFlyabilitySkill(skill: SkillPreset): void {
  localStorage.setItem(FLYABILITY_SKILL_STORAGE_KEY, skill);
}

function isThresholdPair(value: unknown): value is { go: number; marginal: number } {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { go: unknown }).go === "number" &&
    typeof (value as { marginal: unknown }).marginal === "number"
  );
}

/**
 * Persisted thresholds, falling back to the preset for the saved skill level
 * (so the feature works with zero configuration). Saved per-metric tweaks
 * override the preset; any metric missing from older saved data (e.g. the
 * `thermal` metric added later) is backfilled from the preset — so the upgrade
 * is non-breaking and keeps the pilot's existing customizations.
 */
export function getFlyabilityThresholds(): FlyabilityThresholds {
  const preset = FLYABILITY_PRESETS[getFlyabilitySkill()];
  const savedValue = localStorage.getItem(FLYABILITY_THRESHOLDS_STORAGE_KEY);

  if (typeof savedValue === "string") {
    try {
      const parsed = JSON.parse(savedValue) as Record<string, unknown>;
      const pick = (key: keyof FlyabilityThresholds) =>
        isThresholdPair(parsed[key]) ? parsed[key] : preset[key];
      return {
        windSpeed: pick("windSpeed"),
        windGust: pick("windGust"),
        precipitationChance: pick("precipitationChance"),
        cloudCover: pick("cloudCover"),
        thermal: pick("thermal"),
      };
    } catch {
      // fall through to the preset default
    }
  }

  return preset;
}

export function setFlyabilityThresholds(
  thresholds: FlyabilityThresholds,
): void {
  localStorage.setItem(
    FLYABILITY_THRESHOLDS_STORAGE_KEY,
    JSON.stringify(thresholds),
  );
}

export function clearFlyabilityThresholds(): void {
  localStorage.removeItem(FLYABILITY_THRESHOLDS_STORAGE_KEY);
}
